import { Building2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

/**
 * OKS-468 — Compact brand strip for the §04 welcome handoff (Figma `30:173`).
 * Replaces the V1 `<BrandCoverBand>` hero on the Welcome page: a bordered row
 * with the brand's REAL logo (Active Storage `logo_url`, initials fallback —
 * never a literal "P" badge) + "{brand} · welcome".
 *
 * Logo `alt` is brand-scoped for SR users; the fallback icon is decorative.
 */
export interface WelcomeBrandStripProps {
  brandName: string
  logoUrl: string | null
  className?: string
}

export function WelcomeBrandStrip({
  brandName,
  logoUrl,
  className,
}: WelcomeBrandStripProps) {
  const { t } = useTranslation()
  const initials = brandInitials(brandName)

  return (
    <div
      data-testid="welcome-brand-strip"
      className={cn("flex items-center gap-2.5 border-b px-4 py-3", className)}
    >
      <Avatar className="size-7 shrink-0">
        {logoUrl ? (
          <AvatarImage
            src={logoUrl}
            alt={t("wizard.welcome.brand_strip", { brand: brandName })}
          />
        ) : null}
        <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
          {initials || <Building2 className="size-3.5" aria-hidden="true" />}
        </AvatarFallback>
      </Avatar>
      <span
        className="text-sm font-semibold"
        data-testid="welcome-brand-strip-label"
      >
        {t("wizard.welcome.brand_strip", { brand: brandName })}
      </span>
    </div>
  )
}

/** 1–2 uppercase initials from the brand name; empty → icon fallback. */
function brandInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((token) => token[0]?.toUpperCase() ?? "")
    .join("")
}

export default WelcomeBrandStrip
